import * as React from 'react';

import {
  Dialog as ShadcnDialog,
  DialogClose as ShadcnDialogClose,
  DialogContent as ShadcnDialogContent,
  DialogDescription as ShadcnDialogDescription,
  DialogFooter as ShadcnDialogFooter,
  DialogHeader as ShadcnDialogHeader,
  DialogTitle as ShadcnDialogTitle,
  DialogTrigger as ShadcnDialogTrigger,
} from '@/hen-ui/shadcn/dialog';
import {
  modalContentVariants,
  type ModalContentVariantProps,
} from './modal.variants';
import { cn } from '@/hen-ui/utils';

// --- Types ---

export type ModalProps = React.ComponentPropsWithoutRef<typeof ShadcnDialog>;

export interface ModalTriggerProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogTrigger> {}

export interface ModalContentProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogContent>,
    ModalContentVariantProps {}

export interface ModalHeaderProps
  extends React.ComponentPropsWithoutRef<'div'> {}

export interface ModalFooterProps
  extends React.ComponentPropsWithoutRef<'div'> {}

export interface ModalTitleProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogTitle> {}

export interface ModalDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogDescription> {}

export interface ModalCloseProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnDialogClose> {}

// --- Sub-components ---

const ModalRoot = React.forwardRef<
  React.ElementRef<typeof ShadcnDialog>,
  ModalProps
>(({ ...props }, ref) => {
  return <ShadcnDialog {...props} />;
});

ModalRoot.displayName = 'Modal';

export const ModalTrigger = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogTrigger>,
  ModalTriggerProps
>(({ className, ...props }, ref) => {
  return <ShadcnDialogTrigger className={cn(className)} ref={ref} {...props} />;
});

ModalTrigger.displayName = 'ModalTrigger';

export const ModalContent = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogContent>,
  ModalContentProps
>(({ className, size, ...props }, ref) => {
  return (
    <ShadcnDialogContent
      className={cn(modalContentVariants({ size }), className)}
      ref={ref}
      {...props}
    />
  );
});

ModalContent.displayName = 'ModalContent';

export const ModalHeader = React.forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnDialogHeader className={cn(className)} ref={ref} {...props} />
    );
  },
);

ModalHeader.displayName = 'ModalHeader';

export const ModalFooter = React.forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <ShadcnDialogFooter className={cn(className)} ref={ref} {...props} />
    );
  },
);

ModalFooter.displayName = 'ModalFooter';

export const ModalTitle = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogTitle>,
  ModalTitleProps
>(({ className, ...props }, ref) => {
  return <ShadcnDialogTitle className={cn(className)} ref={ref} {...props} />;
});

ModalTitle.displayName = 'ModalTitle';

export const ModalDescription = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogDescription>,
  ModalDescriptionProps
>(({ className, ...props }, ref) => {
  return (
    <ShadcnDialogDescription className={cn(className)} ref={ref} {...props} />
  );
});

ModalDescription.displayName = 'ModalDescription';

export const ModalClose = React.forwardRef<
  React.ElementRef<typeof ShadcnDialogClose>,
  ModalCloseProps
>(({ className, ...props }, ref) => {
  return <ShadcnDialogClose className={cn(className)} ref={ref} {...props} />;
});

ModalClose.displayName = 'ModalClose';

// --- Compound Component ---

export const Modal = ModalRoot as typeof ModalRoot & {
  Trigger: typeof ModalTrigger;
  Content: typeof ModalContent;
  Header: typeof ModalHeader;
  Title: typeof ModalTitle;
  Description: typeof ModalDescription;
  Footer: typeof ModalFooter;
  Close: typeof ModalClose;
};

Modal.Trigger = ModalTrigger;
Modal.Content = ModalContent;
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Description = ModalDescription;
Modal.Footer = ModalFooter;
Modal.Close = ModalClose;
